/**
 * Evaluación pura de respuestas de inspección.
 *
 * El `result` ('pass' | 'fail' | null) se MATERIALIZA al responder y se guarda
 * en `inspection_answer.result`: el histórico queda inmune a cambios de la
 * plantilla y el dashboard no recomputa nada. Estas funciones son puras y
 * tienen unit tests (umbral numérico incluido).
 */

import type { AnswerResult, TemplateItemType } from "./types";

export interface NumberThresholds {
  /** Límite inferior conforme (inclusive). null/undefined = sin límite. */
  min_value?: number | null;
  /** Límite superior conforme (inclusive). null/undefined = sin límite. */
  max_value?: number | null;
}

/**
 * Evalúa un valor numérico contra los umbrales del ítem.
 *
 * Rango conforme: [min_value, max_value] (ambos inclusive). Un límite null se
 * ignora. Valores no finitos (NaN/±Infinity) nunca pasan: un dato corrupto no
 * debe colar como conforme.
 */
export function evaluateNumber(value: number, thresholds: NumberThresholds): AnswerResult {
  if (!Number.isFinite(value)) {
    return "fail";
  }
  const min = thresholds.min_value;
  if (min !== null && min !== undefined && value < min) {
    return "fail";
  }
  const max = thresholds.max_value;
  if (max !== null && max !== undefined && value > max) {
    return "fail";
  }
  return "pass";
}

export interface AnswerSnapshot {
  /** 1/true = sí, 0/false = no (ítems yes_no). */
  value_bool?: number | boolean | null;
  value_number?: number | null;
  value_text?: string | null;
  /** Marcado como "no aplica". */
  is_na?: number | boolean | null;
  /** Nº de fotos adjuntas (ítems photo). */
  photoCount?: number;
}

export interface ItemSnapshot extends NumberThresholds {
  item_type: TemplateItemType | string | null;
}

function isTruthyFlag(v: number | boolean | null | undefined): boolean {
  return v === true || v === 1;
}

/**
 * Resultado materializable de una respuesta.
 *
 * - `null` = sin responder aún (o N/A): no computa como pass ni como fail.
 * - yes_no: sí → pass, no → fail.
 * - number: umbrales inclusive (ver evaluateNumber).
 * - text: cualquier texto no vacío → pass (no hay "texto incorrecto" en F1).
 * - photo: pass cuando hay al menos una foto.
 */
export function evaluateAnswer(item: ItemSnapshot, answer: AnswerSnapshot): AnswerResult | null {
  if (isTruthyFlag(answer.is_na)) {
    return null;
  }

  switch (item.item_type) {
    case "yes_no": {
      if (answer.value_bool === null || answer.value_bool === undefined) {
        return null;
      }
      return isTruthyFlag(answer.value_bool) ? "pass" : "fail";
    }
    case "number": {
      if (answer.value_number === null || answer.value_number === undefined) {
        return null;
      }
      return evaluateNumber(answer.value_number, item);
    }
    case "text": {
      const text = answer.value_text?.trim();
      return text ? "pass" : null;
    }
    case "photo": {
      return (answer.photoCount ?? 0) > 0 ? "pass" : null;
    }
    default: {
      return null;
    }
  }
}

export interface AnsweredCheck {
  template_item_id: string | null;
  result: string | null;
  is_na: number | boolean | null;
  value_bool: number | boolean | null;
  value_number: number | null;
  value_text: string | null;
}

export interface RequiredItemCheck {
  id: string;
  item_type: string | null;
  required: number | boolean | null;
  photo_required: number | boolean | null;
  condition_item_id?: string | null;
  condition_value?: string | null;
}

/** ¿Cuenta esta respuesta como "respondida" para el ítem dado? */
export function isAnswered(item: RequiredItemCheck, answer: AnsweredCheck | undefined): boolean {
  if (!answer) {
    return false;
  }
  if (isTruthyFlag(answer.is_na)) {
    return true;
  }
  switch (item.item_type) {
    case "yes_no": {
      return answer.value_bool !== null && answer.value_bool !== undefined;
    }
    case "number": {
      return answer.value_number !== null && answer.value_number !== undefined;
    }
    case "text": {
      return Boolean(answer.value_text?.trim());
    }
    case "photo": {
      // El "valor" de un ítem photo son sus fotos: result pass se materializa al adjuntar.
      return answer.result === "pass";
    }
    default: {
      return false;
    }
  }
}

// ─── Lógica condicional ──────────────────────────────────────────────────────

/** Valores que puede tomar `condition_value` (respuesta del ítem gobernante). */
export const CONDITION_VALUES = ["yes", "no"] as const;
export type ConditionValue = (typeof CONDITION_VALUES)[number];

export function isConditionValue(v: string | null | undefined): v is ConditionValue {
  return v === "yes" || v === "no";
}

export interface ConditionalItem {
  id: string;
  /** Ítem (yes_no) cuya respuesta gobierna la visibilidad. NULL = siempre visible. */
  condition_item_id?: string | null;
  /** Valor de la respuesta gobernante que REVELA este ítem ('yes' | 'no'). */
  condition_value?: string | null;
}

export interface ConditionAnswer {
  value_bool: number | boolean | null;
  is_na: number | boolean | null;
}

/**
 * ¿Es VISIBLE este ítem dada la respuesta de su ítem gobernante?
 *
 * - Sin `condition_item_id` → siempre visible.
 * - Con condición: visible solo si el ítem gobernante está respondido (yes/no,
 *   no N/A) y su valor coincide con `condition_value`. Un gobernante sin
 *   responder mantiene el dependiente OCULTO (no se evalúa lo desconocido).
 */
export function isItemVisible(
  item: ConditionalItem,
  governingAnswer: ConditionAnswer | undefined,
): boolean {
  const governorId = item.condition_item_id;
  if (!governorId) {
    return true;
  }
  if (!governingAnswer || isTruthyFlag(governingAnswer.is_na)) {
    return false;
  }
  const expected = item.condition_value;
  if (!isConditionValue(expected)) {
    // Condición mal definida (gobernante pero sin valor): ítem siempre oculto.
    return false;
  }
  const answeredYes = isTruthyFlag(governingAnswer.value_bool);
  return expected === "yes" ? answeredYes : !answeredYes;
}

// ─── Scoring ─────────────────────────────────────────────────────────────────

export interface ScorableItem {
  id: string;
  /** Puntos que aporta el ítem cuando su resultado es PASS. NULL/0 = no puntúa. */
  score?: number | null;
}

export interface ScoreAnswer {
  result: string | null;
}

export interface InspectionScore {
  /** Puntos obtenidos (suma de `score` de los ítems VISIBLES que pasaron). */
  earned: number;
  /** Puntos posibles (suma de `score` de los ítems VISIBLES y puntuables). */
  possible: number;
  /** % conformidad (0–100). 0 cuando no hay puntos posibles. */
  percentage: number;
}

/**
 * Calcula el puntaje de una inspección (función pura, con unit tests).
 *
 * Solo cuentan los ítems VISIBLES (los ocultos por condición no suman ni al
 * numerador ni al denominador) y con `score > 0`. `earned` suma el score de
 * los que tienen `result === 'pass'`; `possible` suma el score de todos los
 * puntuables visibles. El porcentaje se redondea al entero más cercano.
 */
export function computeInspectionScore(
  items: readonly (ScorableItem & ConditionalItem)[],
  answersByItem: ReadonlyMap<string, ScoreAnswer & ConditionAnswer>,
): InspectionScore {
  let earned = 0;
  let possible = 0;
  for (const item of items) {
    const { score } = item;
    if (score === null || score === undefined || !(score > 0)) {
      continue;
    }
    const governing = item.condition_item_id
      ? answersByItem.get(item.condition_item_id)
      : undefined;
    if (!isItemVisible(item, governing)) {
      continue;
    }
    possible += score;
    if (answersByItem.get(item.id)?.result === "pass") {
      earned += score;
    }
  }
  const percentage = possible > 0 ? Math.round((earned / possible) * 100) : 0;
  return { earned, possible, percentage };
}

export interface CompletionBlocker {
  itemId: string;
  reason: "unanswered" | "photo_missing";
}

/**
 * Valida si una inspección puede completarse: todos los ítems `required`
 * respondidos (o N/A) y todos los `photo_required` con al menos una foto.
 *
 * Los ítems OCULTOS por lógica condicional se ignoran: un ítem que la
 * respuesta del inspector no reveló no puede bloquear el cierre.
 */
export function getCompletionBlockers(
  items: readonly RequiredItemCheck[],
  answersByItem: ReadonlyMap<string, AnsweredCheck>,
  photoCountByItem: ReadonlyMap<string, number>,
): CompletionBlocker[] {
  const blockers: CompletionBlocker[] = [];
  for (const item of items) {
    const governing = item.condition_item_id
      ? answersByItem.get(item.condition_item_id)
      : undefined;
    if (!isItemVisible(item, governing)) {
      continue;
    }
    const answer = answersByItem.get(item.id);
    if (isTruthyFlag(item.required) && !isAnswered(item, answer)) {
      blockers.push({ itemId: item.id, reason: "unanswered" });
      continue;
    }
    const isNa = answer ? isTruthyFlag(answer.is_na) : false;
    if (isTruthyFlag(item.photo_required) && !isNa && (photoCountByItem.get(item.id) ?? 0) === 0) {
      blockers.push({ itemId: item.id, reason: "photo_missing" });
    }
  }
  return blockers;
}
